import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import SeoHead from '@/components/base/SeoHead';
import JsonLd from '@/components/base/JsonLd';
import { categories } from '@/mocks/categories';
import { getRecommendedComics, getPopularComics, getLatestComics } from '@/mocks/comics';

export default function Home() {
  const recommended = getRecommendedComics(6);
  const popular = getPopularComics(8);
  const latest = getLatestComics(8);

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '韩漫网',
    url: 'https://www.hanmanwang.com',
    description: '韩漫网——领先的中文韩漫阅读平台，提供免费下拉式韩漫阅读体验，日更不断，资源全面。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.hanmanwang.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫网',
    url: 'https://www.hanmanwang.com',
    description: '韩漫网——领先的中文韩漫阅读平台，提供免费下拉式韩漫阅读体验。',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@hanmanwang.com',
      contactType: 'customer service',
    },
  };

  return (
    <>
      <SeoHead
        title="韩漫网_韩漫漫画网站_免费观看下拉式韩漫_漫画韩漫网址"
        description="韩漫漫画网站，免费观看下拉式韩漫资源。韩漫网网址稳定内容全面，无需下载即可畅读。提供恋爱、奇幻、动作、悬疑等海量韩漫，每日更新，是漫画爱好者探索新作的理想选择。"
        keywords="韩漫漫画网站,免费观看下拉式,漫画韩漫网址,韩漫网,韩漫,韩国漫画,韩漫在线阅读"
        canonicalUrl="https://www.hanmanwang.com"
      />
      <JsonLd data={websiteSchema} />
      <JsonLd data={organizationSchema} />

      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative pt-16">
            <div className="relative h-[420px] md:h-[520px] overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20illustration%20with%20vibrant%20colors%2C%20dynamic%20composition%2C%20multiple%20characters%20in%20action%20poses%2C%20warm%20orange%20and%20coral%20tones%2C%20artistic%20watercolor%20and%20ink%20style%2C%20dramatic%20lighting%2C%20comic%20panel%20aesthetic%2C%20clean%20artistic%20background%20with%20subtle%20texture&width=1600&height=800&seq=hero-bg-2026&orientation=landscape"
                alt="韩漫网 - 免费韩漫在线阅读"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
                <h1 className="text-3xl md:text-5xl font-bold text-background-50 mb-4 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                  韩漫漫画网站
                </h1>
                <p className="text-base md:text-lg text-background-50/90 max-w-2xl mb-8 leading-relaxed">
                  免费观看下拉式韩漫，无需下载即可畅读。韩漫网网址稳定、内容全面，是漫画爱好者探索新作的理想选择
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/category/romance"
                    className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    开始阅读
                  </Link>
                  <Link
                    to="/category/fantasy"
                    className="px-8 py-3 bg-background-50/20 backdrop-blur-sm text-background-50 border border-background-50/30 rounded-full font-medium hover:bg-background-50/30 transition-colors whitespace-nowrap"
                  >
                    热门排行
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Category Section */}
          <section className="py-12 md:py-16 px-4 md:px-6 max-w-7xl mx-auto">
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                漫画分类
              </h2>
              <p className="text-foreground-500 mt-2">探索不同类型的韩漫，找到你最爱的那一款</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-3 p-5 md:p-6 rounded-xl bg-background-50 border border-background-200 hover:border-primary-300 hover:bg-primary-50/30 transition-all duration-300"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                    <i className={`${cat.icon} text-xl md:text-2xl`}></i>
                  </div>
                  <span className="text-sm md:text-base font-medium text-foreground-800 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          </section>

          {/* Recommended Section */}
          <section className="py-12 md:py-16 px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  编辑推荐
                </h2>
                <p className="text-foreground-500 mt-2">精选高分韩漫，品质保证</p>
              </div>
              <Link
                to="/category/drama"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1"
              >
                查看更多
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 md:gap-4">
              {recommended.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </section>

          {/* Popular Section */}
          <section className="py-12 md:py-16 bg-background-100">
            <div className="px-4 md:px-6 max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                    热门排行
                  </h2>
                  <p className="text-foreground-500 mt-2">最受读者欢迎的韩漫作品</p>
                </div>
                <Link
                  to="/category/action"
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1"
                >
                  查看更多
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
                {popular.map((comic, index) => (
                  <div key={comic.id} className="relative">
                    <div className="absolute top-2 left-2 z-10 w-7 h-7 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-xs font-bold">
                      {index + 1}
                    </div>
                    <ComicCard comic={comic} />
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Latest Section */}
          <section className="py-12 md:py-16 px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  最新更新
                </h2>
                <p className="text-foreground-500 mt-2">每日更新，精彩不断</p>
              </div>
              <Link
                to="/category/fantasy"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1"
              >
                查看更多
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
              {latest.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-14 md:py-20 bg-primary-500">
            <div className="px-4 md:px-6 max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-4xl font-bold text-background-50 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                开始你的韩漫之旅
              </h2>
              <p className="text-base md:text-lg text-background-50/90 mb-8 leading-relaxed">
                数千部韩漫等你来读，完全免费，无需下载，随时随地畅享阅读乐趣
              </p>
              <Link
                to="/category/romance"
                className="inline-flex px-8 py-3 bg-background-50 text-primary-600 rounded-full font-semibold hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                立即阅读
              </Link>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-12 md:py-16 px-4 md:px-6 max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                常见问题
              </h2>
            </div>
            <div className="space-y-4">
              {faqItems.map((faq, i) => (
                <details key={i} className="group bg-background-50 border border-background-200 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                    <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">{faq.q}</span>
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-180 transition-transform">
                      <i className="ri-arrow-down-s-line"></i>
                    </div>
                  </summary>
                  <div className="px-6 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}

const faqItems = [
  { q: '韩漫网的漫画都是免费的吗？', a: '是的，韩漫网所有漫画内容完全免费，无需付费或订阅即可畅读全部韩漫资源。我们致力于为漫画爱好者提供高质量的免费阅读体验。' },
  { q: '需要下载APP才能看漫画吗？', a: '不需要！韩漫网采用响应式网页设计，直接在浏览器中即可阅读，无需下载任何应用。无论是电脑、平板还是手机，都能获得流畅的下拉式阅读体验。' },
  { q: '漫画更新频率是怎样的？', a: '韩漫网每日更新，保持与韩国最新连载同步。您可以在"最新更新"板块查看当天更新的漫画，确保不错过任何精彩内容。' },
  { q: '什么是下拉式阅读？', a: '下拉式阅读（也叫条漫模式）是韩漫特有的阅读方式。所有漫画内容纵向排列，您只需向下滚动即可连续阅读，无需翻页，让阅读体验更加流畅自然。' },
];